import { useState, useCallback, useEffect } from 'react';
import { VideoFile, ProcessingProgress, VideoSettings } from '../types';
import JSZip from 'jszip';

export function useVideoConverter() {
  const [videos, setVideos] = useState<VideoFile[]>([]);
  const [settings, setSettings] = useState<VideoSettings>({
    format: 'mp4',
    quality: 'medium',
    resolution: 'original'
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState<ProcessingProgress>({ current: 0, total: 0 });

  const createVideoPreview = useCallback((file: File): Promise<{ preview: string; duration?: number }> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      
      video.preload = 'metadata';
      video.muted = true;
      
      video.onloadedmetadata = () => {
        // Capturar frame aos 2 segundos ou 10% da duração
        const seekTime = Math.min(2, video.duration * 0.1);
        video.currentTime = seekTime;
      };
      
      video.onseeked = () => {
        try {
          // Criar thumbnail do vídeo
          canvas.width = Math.min(video.videoWidth, 320);
          canvas.height = Math.min(video.videoHeight, 240);
          
          // Manter proporção
          const aspectRatio = video.videoWidth / video.videoHeight;
          if (canvas.width / canvas.height > aspectRatio) {
            canvas.width = canvas.height * aspectRatio;
          } else {
            canvas.height = canvas.width / aspectRatio;
          }
          
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          
          // Limpar URL do objeto após uso
          URL.revokeObjectURL(video.src);
          
          resolve({
            preview: canvas.toDataURL('image/jpeg', 0.7),
            duration: video.duration
          });
        } catch (error) {
          console.error('Erro ao criar preview do vídeo:', error);
          // Fallback: usar ícone genérico
          resolve({
            preview: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjI0MCIgdmlld0JveD0iMCAwIDMyMCAyNDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMjQwIiBmaWxsPSIjNEE0QTY5Ii8+Cjxwb2x5Z29uIHBvaW50cz0iMTIwLDgwIDE4MCwxMjAgMTIwLDE2MCIgZmlsbD0iI0VBRUNGMyIvPgo8L3N2Zz4=',
            duration: video.duration
          });
        }
      };
      
      video.onerror = () => {
        console.error('Erro ao carregar vídeo para preview');
        resolve({
          preview: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjI0MCIgdmlld0JveD0iMCAwIDMyMCAyNDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMjQwIiBmaWxsPSIjNEE0QTY5Ii8+Cjxwb2x5Z29uIHBvaW50cz0iMTIwLDgwIDE4MCwxMjAgMTIwLDE2MCIgZmlsbD0iI0VBRUNGMyIvPgo8L3N2Zz4='
        });
      };
      
      video.src = URL.createObjectURL(file);
    });
  }, []);

  const handleFileSelect = useCallback(async (files: FileList) => {
    console.log('Selecionando arquivos de vídeo:', files.length);
    
    const validFiles = Array.from(files).filter(file => {
      const extension = file.name.split('.').pop()?.toLowerCase() || '';
      const isValidFormat = ['mp4', 'webm', 'ogv', 'm4v', 'mov', 'avi', 'mkv', 'flv'].includes(extension);
      
      if (!isValidFormat) {
        console.warn(`Formato não suportado: ${file.name}`);
      }
      
      return isValidFormat;
    });

    if (validFiles.length === 0) {
      console.warn('Nenhum arquivo válido selecionado');
      return;
    }

    const newVideos: VideoFile[] = [];
    
    for (const file of validFiles) {
      try {
        // Verificar se o arquivo já existe na lista (por nome, tamanho e última modificação)
        const isDuplicate = videos.some(existingVideo => 
          existingVideo.file.name === file.name && 
          existingVideo.file.size === file.size &&
          existingVideo.file.lastModified === file.lastModified
        );
        
        if (isDuplicate) {
          console.log(`Arquivo já existe na lista: ${file.name} (mesmo nome, tamanho e data)`);
          continue;
        }
        
        console.log(`Processando preview para: ${file.name}`);
        const { preview, duration } = await createVideoPreview(file);
        
        newVideos.push({
          id: Math.random().toString(36).substr(2, 9),
          file,
          preview,
          originalDuration: duration,
          status: 'pending',
          fileType: 'video'
        });
        
        console.log(`Vídeo adicionado: ${file.name}`);
      } catch (error) {
        console.error(`Erro ao processar arquivo ${file.name}:`, error);
      }
    }

    if (newVideos.length > 0) {
      setVideos(prev => [...prev, ...newVideos]);
      console.log(`${newVideos.length} vídeo(s) adicionado(s) com sucesso`);
    }
  }, [createVideoPreview, videos]);

  // Simulação de conversão de vídeo (em uma implementação real, usaria FFmpeg.js)
  const convertVideo = useCallback((videoFile: VideoFile): Promise<{ blob: Blob; format: string }> => {
    return new Promise((resolve, reject) => {
      // NOTA: Esta é uma implementação simulada
      // Em produção, você usaria FFmpeg.js ou um serviço backend
      
      console.log(`Convertendo vídeo: ${videoFile.file.name} para ${settings.format}`);
      
      // Simular processamento
      setTimeout(() => {
        try {
          // Para demonstração, vamos "converter" copiando o arquivo original
          // Em uma implementação real, aqui seria feita a conversão com FFmpeg.js
          
          const reader = new FileReader();
          reader.onload = () => {
            const arrayBuffer = reader.result as ArrayBuffer;
            const blob = new Blob([arrayBuffer], { 
              type: `video/${settings.format}` 
            });
            
            resolve({ 
              blob, 
              format: settings.format 
            });
          };
          reader.onerror = () => reject(new Error('Erro ao ler arquivo'));
          reader.readAsArrayBuffer(videoFile.file);
          
        } catch (error) {
          reject(new Error(`Erro na conversão: ${error}`));
        }
      }, 2000 + Math.random() * 3000); // Simular tempo de processamento
    });
  }, [settings]);

  const processVideo = useCallback(async (videoFile: VideoFile) => {
    console.log('🔄 ProcessVideo INDIVIDUAL chamado para:', videoFile.file?.name);
    
    setVideos(prev => prev.map(vid => 
      vid.id === videoFile.id ? { ...vid, status: 'processing' } : vid
    ));

    try {
      console.log('🔄 Chamando convertVideo para:', videoFile.file?.name);
      const { blob } = await convertVideo(videoFile);
      console.log('✅ ConvertVideo concluído para:', videoFile.file?.name, 'tamanho:', blob.size);
      
      // Criar um novo arquivo processado
      const originalNameWithoutExt = videoFile.file.name.replace(/\.[^/.]+$/, '');
      const processedFile = new File([blob], 
        `${originalNameWithoutExt}.${settings.format}`, 
        { type: `video/${settings.format}` }
      );
      
      console.log('✅ Atualizando estado para completed:', videoFile.file?.name);
      setVideos(prev => prev.map(vid => 
        vid.id === videoFile.id 
          ? { 
              ...vid, 
              status: 'completed', 
              processedFile
            } 
          : vid
      ));
      console.log('✅ ProcessVideo concluído com sucesso para:', videoFile.file?.name);
    } catch (error) {
      console.error('❌ Erro ao processar vídeo:', videoFile.file?.name, error);
      setVideos(prev => prev.map(vid => 
        vid.id === videoFile.id ? { ...vid, status: 'error' } : vid
      ));
    }
  }, [convertVideo, settings.format]);

  const processVideos = useCallback(async (videosToProcess?: VideoFile[]) => {
    console.log('🚀 ProcessVideos CHAMADO!', {
      videosToProcessParam: videosToProcess?.length || 'undefined',
      totalVideosInState: videos.length,
      videosWithPendingStatus: videos.filter(vid => vid.status === 'pending').length,
      videosWithFiles: videos.filter(vid => vid.file).length
    });

    // Filtrar apenas vídeos válidos (com arquivo e status pending)
    const targetVideos = videosToProcess || videos.filter(vid => 
      vid.status === 'pending' && vid.file
    );
    
    console.log('🔄 ProcessVideos: Iniciando processamento em lote', {
      totalVideos: videos.length,
      targetVideos: targetVideos.length,
      videosToProcess: videosToProcess?.length || 'não especificado',
      videosParaProcessar: targetVideos.map(v => ({
        id: v.id,
        name: v.file?.name,
        status: v.status,
        hasFile: !!v.file
      }))
    });

    if (targetVideos.length === 0) {
      console.log('⚠️ ProcessVideos: Nenhum vídeo para processar');
      return;
    }

    console.log(`🎬 ProcessVideos: Iniciando conversão de ${targetVideos.length} vídeo(s)`);
    setIsProcessing(true);
    setProgress({ current: 0, total: targetVideos.length });

    for (let i = 0; i < targetVideos.length; i++) {
      const videoFile = targetVideos[i];
      console.log(`🔄 ProcessVideos: Processando vídeo ${i + 1}/${targetVideos.length}: ${videoFile.file.name}`);
      
      setProgress({ 
        current: i + 1, 
        total: targetVideos.length, 
        currentFileName: videoFile.file.name 
      });

      try {
        await processVideo(videoFile);
        console.log(`✅ ProcessVideos: Vídeo ${videoFile.file.name} processado com sucesso`);
      } catch (error) {
        console.error(`❌ ProcessVideos: Erro ao processar vídeo ${videoFile.file.name}:`, error);
      }
    }

    console.log('🎉 ProcessVideos: Processamento em lote concluído!');
    setIsProcessing(false);
    setProgress({ current: 0, total: 0 });
  }, [videos, processVideo]);

  const downloadVideo = useCallback((videoFile: VideoFile) => {
    if (!videoFile.processedFile) return;

    const url = URL.createObjectURL(videoFile.processedFile);
    const a = document.createElement('a');
    a.href = url;
    a.download = videoFile.processedFile.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  const downloadAll = useCallback(() => {
    const completedVideos = videos.filter(vid => vid.status === 'completed');
    completedVideos.forEach(vid => downloadVideo(vid));
  }, [videos, downloadVideo]);

  const downloadAllAsZip = useCallback(async () => {
    const completedVideos = videos.filter(vid => vid.status === 'completed');
    if (completedVideos.length === 0) return;

    const zip = new JSZip();
    
    // Adicionar cada vídeo ao ZIP
    completedVideos.forEach((videoFile) => {
      if (videoFile.processedFile) {
        const fileName = videoFile.processedFile.name;
        zip.file(fileName, videoFile.processedFile);
      }
    });

    // Gerar e baixar o ZIP
    try {
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `videos_convertidos_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao criar ZIP:', error);
    }
  }, [videos]);

  const removeVideo = useCallback((id: string) => {
    console.log(`Removendo vídeo: ${id}`);
    
    // Encontrar o vídeo antes de removê-lo para limpeza de recursos
    const videoToRemove = videos.find(vid => vid.id === id);
    
    if (videoToRemove) {
      // Limpar URLs de objeto para evitar vazamentos de memória
      if (videoToRemove.preview && videoToRemove.preview.startsWith('blob:')) {
        URL.revokeObjectURL(videoToRemove.preview);
      }
      
      if (videoToRemove.processedFile) {
        // Limpar referências do arquivo processado se necessário
        console.log(`Limpando arquivo processado: ${videoToRemove.processedFile.name}`);
      }
      
      console.log(`Vídeo removido: ${videoToRemove.file.name}`);
    }
    
    setVideos(prev => prev.filter(vid => vid.id !== id));
  }, [videos]);

  const clearAll = useCallback(() => {
    console.log('Limpando todos os vídeos');
    
    // Limpar todos os URLs de objeto para evitar vazamentos de memória
    videos.forEach(video => {
      if (video.preview && video.preview.startsWith('blob:')) {
        URL.revokeObjectURL(video.preview);
      }
    });
    
    setVideos([]);
    console.log('Todos os vídeos foram removidos');
  }, [videos]);

  const updateSettings = useCallback((key: keyof VideoSettings, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  // Debug: Monitorar mudanças no estado dos vídeos
  useEffect(() => {
    console.log('Estado dos vídeos atualizado:', {
      total: videos.length,
      pending: videos.filter(v => v.status === 'pending').length,
      processing: videos.filter(v => v.status === 'processing').length,
      completed: videos.filter(v => v.status === 'completed').length,
      error: videos.filter(v => v.status === 'error').length,
      videoNames: videos.map(v => v.file.name)
    });
  }, [videos]);

  return {
    videos,
    settings,
    isProcessing,
    progress,
    handleFileSelect,
    processVideo,
    processVideos,
    downloadVideo,
    downloadAll,
    downloadAllAsZip,
    removeVideo,
    clearAll,
    updateSettings
  };
} 